import { useState, useEffect } from "react";
import { PushToTalk } from "./components/PushToTalk";
import { RecordButton } from "./components/RecordButton";
import { Console } from "./components/Console";
import { AudioSourceToggle } from "./components/AudioSourceToggle";
import { DefaultAudioInput, AudioInputSource } from "./types/audio";
import { Card } from "./components/ui/card";
import { ArrowRight } from "lucide-react";
import { useAudioRecorder } from "./hooks/useAudioRecorder";
import { useScreenScale } from "./hooks/useScreenScale";
import { VolumeControl } from "./components/VolumeControl";

// Check if we're actually on an LG TV by checking for luna service
const isLGTV = Boolean(window.webOS && window.webOS.platform.tv === true);

// Feature flag for Push-to-Talk
const ENABLE_PTT = import.meta.env.VITE_ENABLE_PTT === "true";

export function App() {
  const [audioSource, setAudioSource] = useState<AudioInputSource>(
    () => new DefaultAudioInput(),
  );
  const { isRecording, isPlaying, error, toggleRecording } =
    useAudioRecorder(audioSource);
  const scale = useScreenScale();

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "ArrowRight") {
        toggleRecording();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [toggleRecording]);

  return (
    <div className="fixed inset-0 flex items-center justify-center bg-black overflow-hidden">
      <div
        className="w-[1920px] h-[1080px] origin-center"
        style={{
          transform: `scale(${scale})`,
        }}
      >
        <div className="w-full h-full p-8 text-white">
          <div className="grid h-full grid-cols-[minmax(400px,_1fr)_minmax(50%,_1fr)] gap-6">
            {/* Main Recording Area */}
            <div className="flex flex-col items-center justify-center gap-8">
              {/* Audio Controls Area */}
              <div className="flex flex-col items-center gap-6 mb-4">
                {/* Audio Source Toggle */}
                <div className="flex items-center justify-center gap-3 text-gray-400">
                  <span className="text-sm font-medium">Mic API:</span>
                  <AudioSourceToggle
                    audioSource={audioSource}
                    onSourceChange={setAudioSource}
                    disabled={!isLGTV}
                  />
                </div>
                
                {/* Volume Control */}
                <VolumeControl />
              </div>

              {/* Push to Talk Area - Feature Flagged */}
              {ENABLE_PTT && (
                <div className="w-full">
                  <PushToTalk audioSource={audioSource} />
                </div>
              )}

              {/* Quick Record Button */}
              <div className="flex flex-col items-center gap-4">
                <div className="w-40 h-40">
                  <RecordButton
                    isRecording={isRecording}
                    isPlaying={isPlaying}
                    error={error}
                    onToggle={toggleRecording}
                  />
                </div>
                <div className="flex items-center gap-2 text-gray-400">
                  <p>Click button above or press</p>
                  <div className="flex h-8 w-8 items-center justify-center rounded bg-gray-800">
                    <ArrowRight className="h-4 w-4" />
                  </div>
                  <p>on your remote</p>
                </div>
              </div>
            </div>

            {/* Console */}
            <Card className="border-gray-800 bg-gray-900 p-4">
              <Console />
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
}
